import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { apiFetch } from "../api_call";
import "./Dashboard.css";

export default function FinalEditSelfCompliance() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    slfcmp_id: "",
    compliance_name: "",
    start_date: "",
    end_date: "",
    description: "",
    comply_by: "",
    reminder_days: 1,
    escalation_email: "",
    escalation_reminder_days: "",
  });

  const [successMsg, setSuccessMsg] = useState("");
  const [errors, setErrors] = useState({});
  const [errorVisible, setErrorVisible] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);

  // 🔹 Format date for <input type="date">
  const toInputDate = (dateStr) => {
    if (!dateStr) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  };

  // 🔹 Fetch custom compliance for edit
  useEffect(() => {
    const slfcmpId = localStorage.getItem("slfcmpId");
    if (!slfcmpId) return;

    const fetchData = async () => {
      try {
        const res = await apiFetch(`/compliance/edit/custom/${slfcmpId}`, {
          method: "GET",
        });

        const row = res?.data;
        if (!row) return;

        setFormData({
          slfcmp_id: row.slfcmp_id || "",
          compliance_name:
            row.slfcmp_title ||
            row.slfcmp_particular ||
            row.slfcmp_act ||
            "",
          start_date: toInputDate(row.slfcmp_start_date),
          end_date: toInputDate(row.slfcmp_end_date),
          description:
            row.slfcmp_description || row.slfcmp_long_description || "",
          comply_by: toInputDate(row.slfcmp_action_date),
          reminder_days: Number(row.slfcmp_reminder_days || 1),
          escalation_email: row.slfcmp_escalation_email || "",
          escalation_reminder_days:
            row.slfcmp_escalation_reminder_days || "",
        });
      } catch (err) {
        console.error("Failed to fetch self compliance:", err);
        setErrors({ api: "Failed to load compliance details." });
      }
    };

    fetchData();
  }, []);

  // 🔹 Handle editable fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSuccessMsg("");
    setErrors({});
  };

  // 🔹 Submit edited data
 // 🔹 Helper to convert YYYY-MM-DD -> DD-MM-YYYY
const toAPIDate = (dateStr) => {
  if (!dateStr) return "";
  const [yyyy, mm, dd] = dateStr.split("-");
  return `${dd}-${mm}-${yyyy}`;
};

const handleSubmit = async (e) => {
  e.preventDefault();
   window.scrollTo({ top: 0, behavior: "smooth" });

  setSuccessMsg("");
  setErrors({});

  const slfcmpId = localStorage.getItem("slfcmpId");
  if (!slfcmpId) {
    setErrors({ api: "Compliance ID missing" });
    return;
  }

  const payload = {
    slfcmp_action_date: toAPIDate(formData.comply_by),
    slfcmp_reminder_days: Number(formData.reminder_days),
    slfcmp_escalation_email: formData.escalation_email || null,
    slfcmp_escalation_reminder_days:
      formData.escalation_reminder_days !== ""
        ? Number(formData.escalation_reminder_days)
        : 0,
  };

  try {
    const res = await apiFetch(`/compliance/edit/custom/${slfcmpId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });

    if (res?.message === "Custom compliance updated successfully") {
      setSuccessMsg("✅ Compliance updated successfully");

      // 🔹 Clear form after success
      setFormData({
        slfcmp_id: "",
        compliance_name: "",
        start_date: "",
        end_date: "",
        description: "",
        comply_by: "",
        reminder_days: 1,
        escalation_email: "",
        escalation_reminder_days: "",
      });

      // 🔹 Redirect after a short delay (so user sees success message)
      setTimeout(() => {
        navigate("/edit-selfcompliance");
      }, 1000);
    } else {
      setErrors({ api: "❌ Update failed" });
    }
  } catch (err) {
    console.error("Update failed:", err);
    setErrors({
      api: err?.error || "❌ Only pending compliances can be edited",
    });
  }
};


  // 🔹 Auto-hide error
  useEffect(() => {
    if (errors.api) {
      setErrorVisible(true);
      const timer = setTimeout(() => setErrorVisible(false), 4500);
      const removeTimer = setTimeout(() => setErrors({}), 5000);
      return () => {
        clearTimeout(timer);
        clearTimeout(removeTimer);
      };
    }
  }, [errors.api]);

  // 🔹 Auto-hide success
  useEffect(() => {
    if (successMsg) {
      setSuccessVisible(true);
      const timer = setTimeout(() => setSuccessVisible(false), 4500);
      const removeTimer = setTimeout(() => setSuccessMsg(""), 5000);
      return () => {
        clearTimeout(timer);
        clearTimeout(removeTimer);
      };
    }
  }, [successMsg]);

  return (
    <div className="add-user">
      <UserSidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <UserHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="form-container">
        <h2>Edit Self Compliance</h2>

        <div className="messages-top" style={{ margin: "0 auto 1rem auto", width: "50%" }}>
          {errors.api && (
            <div className={`login-error ${!errorVisible ? "fade-out" : ""}`}>
              {errors.api}
            </div>
          )}
          {successMsg && (
            <div className={`success-msg ${!successVisible ? "fade-out" : ""}`}>
              {successMsg}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="add-user-form">
          {/* 🔒 Locked fields */}
          <label>
            Compliance ID
            <input type="text" value={formData.slfcmp_id} disabled />
          </label>

          <label>
            Compliance Name
            <input type="text" value={formData.compliance_name} disabled />
          </label>

          <label>
            Start Date
            <input type="date" value={formData.start_date} disabled />
          </label>

          <label>
            End Date
            <input type="date" value={formData.end_date} disabled />
          </label>

          <label>
            Comply By (Action Date)
            <input
              type="date"
              name="comply_by"
              value={formData.comply_by}
              onChange={handleInputChange}
            />
          </label><label></label>

          <label>
            Description
            <textarea value={formData.description} disabled rows={3} />
          </label><label></label>

          {/* ✅ Editable fields */}
          <label>
            Reminder Days
            <input
              type="number"
              name="reminder_days"
              min="1"
              value={formData.reminder_days}
              onChange={handleInputChange}
              style={{ width: "60px" }}
            />
          </label><label></label>

          <label >
  Escalation Email
  <input
    type="email"
    value={formData.escalation_email}
  />
</label>


          <label >
  Reminder of Escalation Email
  <input
    type="number"
    value={formData.escalation_reminder_days}
  />
</label>


          <button type="submit" className="submit-btn">
            Update Compliance
          </button>
        </form>
      </div>
    </div>
  );
}
